import styled from '@emotion/styled'
import { Modal, Button, Form, Input, Slider, notification, message } from 'antd'
import BigNumber from 'bignumber.js'
import clsx from 'clsx'
import { useMemo, useState } from 'react'
import { useAccount, useBalance, useChainId, useWriteContract } from 'wagmi'

import error_icon from '@/assets/error_icon.png'
import success_icon from '@/assets/success_icon.png'
import DataLoading from '@/components/DataLoading'
import ToolTipCus from '@/components/Tooltips'
import { config, MAXDAYS, MINDAYS } from '@/config'
import { useLocalStorage } from '@/context/localstorage-context'
import type { PosStaker } from '@/types/pos-staker'
import { formatBalance, isNumber } from '@/utils'
import { useIwan } from '@/utils/useWebSocket'

import { Link } from '../pos-detail.module'

export default function ModifyModal({
  visible,
  onCancel,
  stakerInfo,
  nextLockEpochs,
}: {
  nextLockEpochs: number
  visible: boolean
  onCancel: () => void
  stakerInfo: PosStaker
}) {
  const chainId = useChainId()
  const { address } = useAccount()
  const { theme } = useLocalStorage()
  const [loadingBTn, setLoadingBtn] = useState(false)
  const isdark = useMemo(() => theme === 'dark', [theme])
  const { data, status } = useBalance({ address })
  const [lockTime, setLockTime] = useState<number>(stakerInfo.lockEpochs)
  const [nextLockTime, setNextLockTime] = useState<number>(nextLockEpochs)

  const [form] = Form.useForm()
  const { data: hash, writeContractAsync } = useWriteContract()
  const PosContractConfig = config[chainId]['PosContract']
  const [modifyType, setModifyType] = useState<'lockPeriod' | 'fee'>(
    'lockPeriod',
  )
  const { data: currentEpochInfo } = useIwan(
    ['getCurrentEpochInfo'],
    {},
    modifyType === 'fee',
  )
  const isLoading = useMemo(() => {
    return modifyType === 'fee' && currentEpochInfo === null
  }, [currentEpochInfo])

  const balance = useMemo(() => {
    return data && status === 'success'
      ? formatBalance(data.value.toString(10))
      : '0'
  }, [data, status])

  const handleSliderChange = (value: number) => {
    setNextLockTime(value)
    setLockTime(value)
  }

  const checkFeeRate = async (_: any, value: any) => {
    const maxFee = stakerInfo.maxFeeFmt || '0%'
    const feeRate = stakerInfo.feeRateFmt || '0%'
    try {
      if (!isNumber(value)) {
        return Promise.reject(new Error('Invalid fee rate'))
      }
      if (Number(value) < 0 || Number(value) > Number(maxFee.slice(0, -1))) {
        return Promise.reject(new Error('Invalid fee rate'))
      }
      if (
        value.toString().split('.')[1] &&
        value.toString().split('.')[1].length > 2
      ) {
        return Promise.reject(new Error('Invalid fee rate'))
      }
      if (
        new BigNumber(value).gt(feeRate.slice(0, -1)) &&
        new BigNumber(value).gt(Number(feeRate.slice(0, -1)) + 1)
      ) {
        return Promise.reject(new Error('Invalid fee rate'))
      }
      return Promise.resolve()
    } catch {
      return Promise.reject(new Error('Invalid fee rate'))
    }
  }

  const handleUpdateLockTime = async () => {
    setLoadingBtn(true)
    try {
      const hash = await writeContractAsync({
        address: PosContractConfig.addr,
        abi: PosContractConfig.abi,
        functionName: 'stakeUpdate',
        args: [stakerInfo.address, lockTime],
      })
      notification.success({
        message: 'Transaction completed',
        description: <Link txHash={hash} />,
        className: `${isdark ? 'noticeSuccess_dark' : 'noticeSuccess_light'}`,
        icon: <img width={30} src={success_icon} alt="notification" />,
      })
      onCancel()
    } catch (error) {
      console.error('posStakeRegister', error)
      notification.error({
        message: 'Transaction Sent Failed',
        description: <Link txHash={hash || ''} />,
        className: `${isdark ? 'noticeSuccess_dark' : 'noticeSuccess_light'}`,
        icon: <img width={30} src={error_icon} alt="notification" />,
      })
      onCancel()
    }
  }

  const handleUpdateFeeRate = async () => {
    if (currentEpochInfo.epochId === stakerInfo.feeRateChangedEpoch) {
      message.warn('Only one modification is allowed per day')
      return
    }
    setLoadingBtn(true)
    try {
      const hash = await writeContractAsync({
        address: PosContractConfig.addr,
        abi: PosContractConfig.abi,
        functionName: 'stakeUpdateFeeRate',
        args: [stakerInfo.address, Math.round(form.getFieldValue('fee') * 100)],
      })
      notification.success({
        message: 'Transaction completed',
        description: <Link txHash={hash} />,
        className: `${isdark ? 'noticeSuccess_dark' : 'noticeSuccess_light'}`,
        icon: <img width={30} src={success_icon} alt="notification" />,
      })
      onCancel()
    } catch (error) {
      console.error('posStakeRegister', error)
      notification.error({
        message: 'Transaction Sent Failed',
        description: <Link txHash={hash || ''} />,
        className: `${isdark ? 'noticeSuccess_dark' : 'noticeSuccess_light'}`,
        icon: <img width={30} src={error_icon} alt="notification" />,
      })
      onCancel()
    }
  }

  return (
    <Container>
      <Modal
        className={clsx('stwoTwoSty', {
          'stwoTwoSty-dark darkModal': isdark,
          'stwoTwoSty-light lightModal': !isdark,
        })}
        maskClosable={true}
        closable={false}
        destroyOnClose={true}
        style={{ borderRadius: '16px' }}
        width={700}
        mask={true}
        open={visible}
        title={<Title className="text-light-0 dark:text-white">Modify</Title>}
        footer={null}
        onCancel={onCancel}
      >
        <DataLoading loading={isLoading}>
          <Part isdark={isdark} className="bg-white dark:bg-common-1">
            <PartTitle className="text-light-0 dark:text-white">
              PoS Node Account
            </PartTitle>
            <LineStyle isdark={isdark} />
            <DetailInfo>
              <RowStyle>
                <span>Name</span>
                <AddrText>
                  <ImgStyle
                    width={25}
                    src={`data:image/${stakerInfo.iconType};base64,${stakerInfo.iconData}`}
                    alt="icon"
                  />
                  {stakerInfo.name}
                </AddrText>
              </RowStyle>
              <RowStyle>
                <span className="md:text-left">Validator Address</span>
                <AddrText>{stakerInfo.address}</AddrText>
              </RowStyle>
            </DetailInfo>
            <LineStyle1 isdark={isdark} />
            <DetailInfo>
              <PartTitle2 className="text-light-0 dark:text-white">
                Modified Parameter
              </PartTitle2>
              <div className="flex justify-between items-center gap-[10px]">
                <Button
                  onClick={() => setModifyType('lockPeriod')}
                  className={
                    'w-[48%] rounded-3xl h-[30px] text-[14px] leading-[6px] ' +
                    (modifyType === 'lockPeriod'
                      ? isdark
                        ? 'bg-dark-105 text-dark-111 border-dark-999'
                        : 'bg-light-888 text-dark-111'
                      : isdark
                        ? 'bg-transparent text-dark-0 border-dark-999'
                        : 'bg-white text-light-444')
                  }
                >
                  Lock Period
                </Button>
                <Button
                  disabled={stakerInfo.acceptDelegation !== 'Yes'}
                  onClick={() => setModifyType('fee')}
                  className={
                    'w-[48%] rounded-3xl h-[30px] btn text-[14px] leading-[6px] ' +
                    (modifyType === 'lockPeriod'
                      ? isdark
                        ? 'bg-transparent text-dark-0 border-dark-999'
                        : 'bg-white'
                      : isdark
                        ? 'bg-dark-105 text-dark-111 border-dark-999'
                        : 'bg-light-888')
                  }
                >
                  Delegation Fee(%)
                </Button>
              </div>
              {modifyType === 'lockPeriod' && (
                <InputContainer
                  className={clsx('mt-[10px] flex flex-col gap-6', {
                    'inputDisabled-dark': isdark,
                    'inputDisabled-light': !isdark,
                  })}
                >
                  <Input
                    className="disabledSty"
                    disabled
                    addonBefore="Current Lock Period"
                    value={stakerInfo.lockEpochs}
                  />
                  <Input
                    className="disabledSty"
                    addonBefore="Next Lock Period"
                    disabled
                    value={nextLockTime}
                  />
                  <SliderContainer className="flex w-full border-1 border-light-105 p-[5px] h-[35px] gap-2 dark:border-dark-999 rounded-tl-[10px] rounded-bl-[10px]">
                    <div className="w-full">
                      <Slider
                        step={1}
                        min={MINDAYS}
                        max={MAXDAYS}
                        className="top-[-4px]"
                        defaultValue={nextLockTime}
                        tooltip={{ open: false }}
                        onChange={handleSliderChange}
                      />
                    </div>
                    <div className="whitespace-nowrap text-light-111 dark:text-dark-111">
                      {lockTime} Days
                    </div>
                  </SliderContainer>
                </InputContainer>
              )}
              {modifyType === 'fee' && (
                <InputContainer
                  className={clsx('mt-[10px] flex flex-col gap-6', {
                    'inputDisabled-dark': isdark,
                    'inputDisabled-light': !isdark,
                  })}
                >
                  <Input
                    className="disabledSty"
                    disabled
                    addonBefore="Max Fee(%)"
                    value={stakerInfo.maxFeeFmt}
                  />
                  <Input
                    className="disabledSty"
                    addonBefore="Current Fee(%)"
                    disabled
                    value={stakerInfo.feeRateFmt}
                  />
                  <Form form={form}>
                    <Form.Item
                      name="fee"
                      rules={[{ required: true, validator: checkFeeRate }]}
                    >
                      <Input
                        autoComplete="off"
                        suffix="%"
                        placeholder="0"
                        addonBefore={
                          <div className="pr-4">
                            New Delegation Fee(%)
                            <ToolTipCus
                              title={
                                'Note: Increases are limited to a maximum of 1% per day and cannot exceed the Max Fee. Decreases are unrestricted.'
                              }
                            />
                          </div>
                        }
                      />
                    </Form.Item>
                  </Form>
                </InputContainer>
              )}
            </DetailInfo>
          </Part>
          <Part isdark={isdark} className="bg-white dark:bg-common-1">
            <PartTitle className="text-light-0 dark:text-white">
              My Account
            </PartTitle>
            <LineStyle isdark={isdark} />
            <DetailInfo>
              <RowStyle>
                <span>Amount</span>
                <span>
                  <span style={{ color: '#2FBDF4' }}>{balance} </span>
                  WAN
                </span>
              </RowStyle>
              <RowStyle>
                <span>Address</span>
                <AddrText>{address}</AddrText>
              </RowStyle>
            </DetailInfo>
          </Part>
          <BtnArea isdark={isdark}>
            <Button onClick={onCancel}>Cancel</Button>
            <Button
              onClick={
                modifyType === 'lockPeriod'
                  ? handleUpdateLockTime
                  : handleUpdateFeeRate
              }
              loading={loadingBTn}
            >
              Confirm
            </Button>
          </BtnArea>
        </DataLoading>
      </Modal>
    </Container>
  )
}

const Container = styled.div`
  .ant-input {
    border: 1px solid #2fbdf4;
  }
`

const Title = styled.span`
  font-size: 24px;
  font-family: Inter-Bold, Inter;
  font-weight: 400;
`

const BtnArea = styled.div<{
  isdark: boolean
}>`
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
  gap: 20px;

  .ant-btn:first-of-type {
    background: ${(props) =>
      props.isdark ? 'rgba(255, 255, 255, 0.1)' : '#E5E5E5'};
    border-radius: 16px;
    color: ${(props) => (props.isdark ? '#818d96' : '#999')};
  }
  .ant-btn:first-of-type:hover {
    background: ${(props) =>
      props.isdark ? 'rgba(255, 255, 255, 0.1)' : '#E5E5E5'};
  }
  .ant-btn {
    width: calc((100% - 20px) / 2);
    border-radius: 16px;
    height: 60px;
    color: white;
    border: none;
    background: #0f68aa;
  }
  .ant-btn:hover {
    background: #0f68aa;
  }
`

const Part = styled.div<{
  isdark: boolean
}>`
  border-radius: 16px;
  opacity: 1;
  border: ${(props) =>
    props.isdark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #EBEFF2'};
  margin-bottom: 2rem;
  padding-top: 2rem;

  span {
    color: #818d96;
  }
`

const PartTitle = styled.div`
  font-size: 14px;
  font-family: Inter-Regular, Inter;
  font-weight: 400;
  line-height: 19px;
  background-clip: text;
  margin-left: 2rem;
  text-align: left;
`

const PartTitle2 = styled.div`
  font-size: 14px;
  font-family: Inter-Regular, Inter;
  font-weight: 400;
  line-height: 19px;
  background-clip: text;
  text-align: left;
`

const LineStyle = styled.div<{
  isdark: boolean
}>`
  width: 100%;
  margin: 2rem 0;
  opacity: 0.2;
  border: ${(props) =>
    props.isdark ? '1px solid rgba(255, 255, 255, 0.3)' : '1px solid #EBEFF2'};
`
const LineStyle1 = styled.div<{
  isdark: boolean
}>`
  width: 100%;
  margin: 1rem 0;
  opacity: 0.2;
  border: ${(props) =>
    props.isdark ? '1px solid rgba(255, 255, 255, 0.3)' : '1px solid #EBEFF2'};
`

const DetailInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  padding: 0 2rem 1rem;
  gap: 1rem;
`

const RowStyle = styled.div`
  color: #757575;
  display: flex;
  justify-content: space-between;

  p {
    font-size: 16px;
  }
`

const ImgStyle = styled.img`
  border-radius: 50%;
  border: 1px solid #757575;
  margin-right: 3px;
  display: inline-block;
`

const AddrText = styled.span`
  @media (max-width: 768px) {
    width: 70%;
    white-space: break-spaces;
    text-align: right;
  }
`

const InputContainer = styled.div`
  .ant-input-group-addon {
    color: #0f68aa;
    border-top-left-radius: 10px;
    border-bottom-left-radius: 10px;
  }

  .ant-input {
    height: 35px;
    border-top-right-radius: 10px;
    border-bottom-right-radius: 10px;
  }

  .ant-input:hover {
    border-color: none;
  }
`

const SliderContainer = styled.div`
  border-left: none !important;
  border-top-right-radius: 10px;
  border-bottom-right-radius: 10px;
`
